/* eslint-disable @typescript-eslint/no-floating-promises */
import ReactGA from 'react-ga4';
import { inject, injectable } from 'inversify';
import { type Logger, InjectionSymbols, LogType } from '@hitiq/logger';
import { CognitoUser } from '@aws-amplify/auth';
import {
  clearUser,
  isSyncCompletedSelector,
  isSyncingSelector,
  updateIsSyncCompleted,
  updateIsSyncing,
  dispatchUser,
  loggedInUserSelector,
  isLoadingSelector,
  dispatchCognitoUser,
  cognitoUserSelector,
  authErrorsSelector,
  authLastErrorSelector,
  dispatchIsNetworkConnected,
  dispatchDataStoreError,
  checkIfIsNetworkConnected,
  dataStoreErrorSelector,
} from './auth.slice';
import { store } from '../../store';
import { AmplifyDataStore } from '../../dataStorePersistence/AmplifyDataStore';
import { loginThunk } from './login.thunk';
import { currentAuthenticatedUserThunk } from './currentUser.thunk';
import { AssessmentRepository } from '../../reduxRepositories/assessments/assessmentRepository';
import { PlayerRepository } from '../../reduxRepositories/players/playerRepository';
import { IncidentRepository } from '../../reduxRepositories/incident/incidentRepository';
import { ConneqtRepository } from '../../reduxRepositories/conneqt/conneqtRepository';
import { TeamRepository } from '../../reduxRepositories/teams/teamRepository';
import {
  AccountsRepository,
  NotificationRepository,
} from '../../reduxRepositories';
import { getAppUserThunk } from './getAppUser.thunk';
import { logoutThunk } from './logout.thunk';
import { UserRepository } from '../../reduxRepositories/users/userRepository';
import { syncThunk } from './sync.thunk';
import { DatadogLogger } from '../../logger/datadogLogger';
import {
  User,
  UserCredentials,
} from '../../reduxRepositories/users/users.interfaces';
import { LoggedInUser } from './auth.interfaces';
import { useAppSelector } from '../../hooks';

@injectable()
export class AuthService {
  sub: any | null = null;
  authErrorsSelector = authErrorsSelector;
  authLastErrorSelector = authLastErrorSelector;
  cognitoUserSelector = cognitoUserSelector;
  loggedInUserSelector = loggedInUserSelector;
  isLoadingSelector = isLoadingSelector;
  isSyncCompletedSelector = isSyncCompletedSelector;
  isSyncingSelector = isSyncingSelector;
  // TODO: MOVE INTO SEPARATE SERVICE
  checkIfIsNetworkConnected = checkIfIsNetworkConnected;
  dataStoreErrorSelector = dataStoreErrorSelector;

  constructor(
    private userRepository: UserRepository,
    private assessmentRepository: AssessmentRepository,
    private playerRepository: PlayerRepository,
    private notificationRepository: NotificationRepository,
    private teamRepository: TeamRepository,
    private conneqtRepository: ConneqtRepository,
    private incidentRepository: IncidentRepository,
    private accountsRepository: AccountsRepository,
    private amplifyDataStore: AmplifyDataStore<any>,
    @inject(InjectionSymbols.LOGGER) private logger: Logger,
  ) {
    this.listenToSyncChanges();
  }

  useLoggedInUserSelector = (): LoggedInUser => {
    return useAppSelector(this.loggedInUserSelector);
  };

  listenToIsNetworkConnected() {
    this.amplifyDataStore.onNetworkStatusChanged.subscribe(
      (isNetworkConnected: boolean) => {
        store.dispatch(dispatchIsNetworkConnected(isNetworkConnected));
      },
    );
  }

  listenToSyncChanges() {
    this.amplifyDataStore.onIsSyncedChanged.subscribe((isSynced: boolean) => {
      store.dispatch(updateIsSyncCompleted(isSynced));
    });

    this.amplifyDataStore.onIsSyncingChanged.subscribe((isSyncing: boolean) => {
      store.dispatch(updateIsSyncing(isSyncing));
    });
  }

  listenToDataStoreErrors() {
    this.amplifyDataStore.onDataStoreError.subscribe((error: any) => {
      store.dispatch(dispatchDataStoreError(error));
    });
  }

  clearDataStoreError() {
    store.dispatch(dispatchDataStoreError(''));
  }

  dispatchLogin(user: UserCredentials) {
    // QUESTION: is there any security reason to resetStore on logout?
    // this.resetStore();

    store
      .dispatch(
        loginThunk({
          email: user.email,
          password: user.password,
        }),
      )
      .unwrap()
      .then((cognitoUser) => {
        this.dispatchQueryAppUser(cognitoUser);
      })
      // TODO: check if this is needed so unwrap doesn't fail if error is returned?
      .catch((error) => {});
  }

  dispatchLogout(): void {
    store
      .dispatch(
        logoutThunk({
          amplifyDataStore: this.amplifyDataStore,
          logger: this.logger,
        }),
      )
      .then(() => {
        (this.logger as DatadogLogger).logger?.addContext('subService', 'AUTH');
        this.logger.log(LogType.info, `User logged out.`);
        ReactGA.event({
          category: 'Authentication',
          action: 'Sign Out',
        });
      });
  }

  // This is always called when you reload.
  dispatchCurrentAuthenticatedUser() {
    store
      .dispatch(
        currentAuthenticatedUserThunk({
          logger: this.logger,
        }),
      )
      .unwrap()
      .then((cognitoUser) => {
        // TODO: figure out why error is shown in browser when thunk throws
        if (!cognitoUser) return;
        this.dispatchQueryAppUser(cognitoUser);
      })
      .catch((error) => {
        this.logger.log(
          LogType.error,
          `Auth: dispatchCurrentAuthenticatedUser error: ${error.message}`,
        );
      });
  }

  // TODO: figure out why the cognitoUser is not the right type?
  // TODO: remove the fetching of the user unless we need it
  private dispatchQueryAppUser(cognitoUser: CognitoUser) {
    const userId = cognitoUser.getSignInUserSession()?.getAccessToken()
      .payload.sub;

    if (!userId) {
      return;
    }

    store
      .dispatch(getAppUserThunk(userId))
      .unwrap()
      .then((user: User) => {
        this.logger.log(
          LogType.info,
          `User ${user.accountId} authenticated as ${user.accountType}`,
        );
        // We should find a better way to rebind so we do not have reference to DataDog in this class.
        (this.logger as DatadogLogger).logger?.addContext(
          'subService',
          user.accountType,
        );

        ReactGA.event({
          category: 'Authentication',
          action: 'Sign In',
          label: user.accountType,
        });

        this.startSync(user);
      })
      // This is not needed since user will login if currentAuthenticatedUser does not exist or fail.
      .catch((error) => {
        console.log('Error in dispatch(getAppUserThunk... ', error);
        if (error?.message?.errors && error.message.errors[0]) {
          if (
            error.message.errors[0].message ===
            'Request failed with status code 403'
          ) {
            store.dispatch(dispatchDataStoreError('ACCESS_FORBIDDEN'));
          } else {
            this.logger.log(
              LogType.error,
              `Auth: dispatchQueryAppUser error: ${error}`,
            );
          }
        }
      });
  }

  // TODO: consider a better place for this method. I put it here even though it is not a auth concern to avoid having to return a promise to the loginDomainService (which should be promise free) before calling dispatchUpdateUser. Or maybe it is authorization i.e. sync my data.
  private startSync(user: User) {
    // Hub.listen('datastore' doesn't trigger any events if there is nothing to sync. So data in dataStore will not get loaded into redux if all data is already available locally. It might not be ideal if a lot of data will update locally available data. But this is never happening in Conneqt since not much data is edited. Maybe the user will see 'cleared' for a moment before it is updated to 'not cleared' or the other way around?
    this.loadDataIntoRedux();

    store
      .dispatch(
        syncThunk({
          amplifyDataStore: this.amplifyDataStore,
          logger: this.logger,
        }),
      )
      .unwrap()
      .then((success) => {
        this.loadDataIntoRedux();
        this.setupStore(user);
      })
      // This is not needed
      .catch((error) => {
        console.log('start sync syncThunk dispatch error: ', error);
      });
  }

  private setupStore(user: User): void {
    // It should be possible not to send in the user since this in called after all data is synced but I'm leaving this here for now to make sure we don't have any more issues.
    // const user = this.userSelector(store.getState());
    if (!user) {
      console.error(
        'user need to be set before setting up repositories. Make sure you wait to call setupStore until you have fetched logged in user from app authorization db',
      );
      return;
    }

    // calls DataStore.observe() which forward create, edit and delete events in dataStore to redux which is set up for each data type in method setUpRepository below.
    this.amplifyDataStore.startObservingDataEvents();

    this.userRepository.setUpRepository(user);
    this.assessmentRepository.setUpRepository(user);
    this.incidentRepository.setUpRepository(user);
    this.playerRepository.setUpRepository(user);
    this.notificationRepository.setUpRepository(user);
    this.teamRepository.setUpRepository(user);
    this.conneqtRepository.setUpRepository(user);
    this.accountsRepository.setUpRepository(user);
  }

  private loadDataIntoRedux() {
    this.userRepository.dispatchLoad();
    this.assessmentRepository.dispatchLoad();
    this.incidentRepository.dispatchLoad();
    this.playerRepository.dispatchLoad();
    this.notificationRepository.dispatchLoad();
    this.teamRepository.dispatchLoad();
    this.conneqtRepository.dispatchLoad();
    this.accountsRepository.dispatchLoad();
  }
}
